import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { Check, Image as ImageIcon, Loader2, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRESET_LABEL = {
    declutter: "Opruimen",
    sunny: "Zonnig",
    add_plants: "Planten",
    restyle: "Restyle",
    relight: "Relight",
    blur: "Blurren",
};

const labelFor = (v) => {
    if (v.action_preset && PRESET_LABEL[v.action_preset]) return PRESET_LABEL[v.action_preset];
    if (v.tool_key === "upscale") return "Upscale";
    if (v.comment_nl) return "Aangepast";
    return "Bewerkt";
};

const thumbUrl = (url, refreshKey) => {
    if (!url) return null;
    // Absolute URLs (R2 presigned) must keep their AWS signature query.
    // Don't split("?") on them or the image returns 401.
    if (/^https?:\/\//i.test(url)) {
        return url;
    }
    const clean = url.split("?")[0];
    const m = /\/api\/uploads\/([^/?#]+)$/.exec(clean);
    const base = m ? `/api/thumb/${m[1]}?w=240` : clean;
    const abs = base.startsWith("/") ? `${BACKEND}${base}` : base;
    return `${abs}${abs.includes("?") ? "&" : "?"}rk=${refreshKey || 0}`;
};

/**
 * Horizontal history strip for a single project photo. Shows:
 *  - [Origineel] thumbnail
 *  - One thumbnail per completed AI version (sorted oldest → newest)
 *
 * The currently-active version is highlighted with a yellow ring + ✓ badge.
 * Click any thumbnail → POSTs /set-active → calls `onActiveChanged(newUrl)`.
 *
 * Imperative API (via ref):
 *  - reload() — re-fetch the history (call after a generation completes)
 *
 * Props:
 *  - requestId, photoIdx
 *  - refreshKey         — bumped after a generation to bust thumb cache
 *  - onActiveChanged(newActiveUrl, originalUrl) — fired on click + reload
 *  - data-testid prefix — `history-strip-{photoIdx}`
 */
const PhotoHistoryStrip = forwardRef(function PhotoHistoryStrip({
    requestId, photoIdx, refreshKey = 0, onActiveChanged,
}, ref) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [switching, setSwitching] = useState(null); // url being switched-to

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: res } = await api.get(`/requests/${requestId}/photos/${photoIdx}/history`);
            setData(res);
            return res;
        } catch (e) {
            // noop — strip stays hidden
            return null;
        } finally {
            setLoading(false);
        }
    }, [requestId, photoIdx]);

    useImperativeHandle(ref, () => ({ reload }), [reload]);

    useEffect(() => { reload(); }, [reload, refreshKey]);

    const switchTo = async (url) => {
        if (!data || url === data.active_url) return;
        setSwitching(url);
        try {
            await api.post(`/requests/${requestId}/photos/${photoIdx}/set-active`, { url });
            const next = await reload();
            onActiveChanged?.(url, next?.original_url);
            toast.success("Versie ingesteld als actief");
        } catch (e) {
            const msg = e.response?.data?.detail || "Kon versie niet wisselen";
            toast.error(typeof msg === "string" ? msg : "Kon versie niet wisselen");
        } finally {
            setSwitching(null);
        }
    };

    if (!data) {
        return loading ? (
            <div className="h-24 flex items-center justify-center text-zinc-500 text-xs gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> History laden…
            </div>
        ) : null;
    }

    const versions = (data.versions || []).filter((v) => !v.reverted);
    const hasAny = versions.length > 0;
    const items = [
        {
            type: "original",
            url: data.original_url,
            label: "Origineel",
            key: "origineel",
        },
        ...versions.map((v, idx) => ({
            type: "version",
            url: v.url,
            label: labelFor(v),
            sub: `V${idx + 1}`,
            key: v.job_id,
        })),
    ];

    return (
        <div className="space-y-2" data-testid={`history-strip-${photoIdx}`}>
            <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 inline-flex items-center gap-1.5">
                    <ImageIcon className="h-3 w-3" />
                    Geschiedenis · {hasAny ? `${versions.length} versie${versions.length === 1 ? "" : "s"}` : "geen bewerkingen"}
                </p>
                {data.active_url !== data.original_url && (
                    <button
                        type="button"
                        onClick={() => switchTo(data.original_url)}
                        className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1"
                        data-testid={`history-strip-reset-${photoIdx}`}
                        title="Zet de origineel als actieve foto"
                    >
                        <Undo2 className="h-3 w-3" /> Terug naar origineel
                    </button>
                )}
            </div>
            <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
                {items.map((it) => {
                    const isActive = it.url === data.active_url;
                    const isBusy = switching === it.url;
                    return (
                        <button
                            key={it.key}
                            type="button"
                            onClick={() => switchTo(it.url)}
                            disabled={isBusy || isActive}
                            data-testid={`history-thumb-${photoIdx}-${it.type === "original" ? "original" : it.key}`}
                            className={`relative shrink-0 group rounded-sm overflow-hidden border transition-all ${
                                isActive
                                    ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/40 cursor-default"
                                    : "border-zinc-200 hover:border-zinc-400 active:scale-[0.98]"
                            }`}
                            title={`${it.label}${isActive ? " (actief)" : ""}`}
                        >
                            <img
                                src={thumbUrl(it.url, refreshKey)}
                                alt={it.label}
                                loading="lazy"
                                className="w-24 h-24 object-cover"
                            />
                            {/* bottom gradient label */}
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2 pt-3 pb-1.5">
                                <p className="text-[10px] uppercase tracking-wider text-white font-semibold truncate">
                                    {it.sub ? <span className="text-[#E5A93D] mr-1">{it.sub}</span> : null}
                                    {it.label}
                                </p>
                            </div>
                            {isActive && (
                                <span className="absolute top-1 right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#E5A93D] text-black shadow">
                                    <Check className="h-3 w-3" strokeWidth={3} />
                                </span>
                            )}
                            {isBusy && (
                                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-[#E5A93D]" />
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
});

export default PhotoHistoryStrip;
